import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Users } from "lucide-react";
import { DURATION, EASE } from "@/lib/motion";

/**
 * LiveTicker — infinite horizontal scroll of activity pill cards.
 *
 * Behavior:
 *  - Auto-scrolls right→left at ~30 px/sec (5px/100ms)
 *  - Hover anywhere on the track → immediate pause
 *  - Random card pulses every 4-6s (border glow + counter ping + "now" dot)
 *  - Respects prefers-reduced-motion (no auto-scroll, no pulse)
 *
 * Tokens used:
 *  - DURATION.ui for hover pause
 *  - DURATION.card for pulse ring
 */
const ACTIVITIES = [
  { title: "Brunch crawl",           hood: "Bishop Arts, Dallas",       count: 6 },
  { title: "Trivia night",           hood: "The Marigny, NOLA",         count: 3 },
  { title: "Cedar Ridge hike",       hood: "South Dallas",              count: 8 },
  { title: "Live jazz",              hood: "Frenchmen St, NOLA",        count: 12 },
  { title: "Pickleball — need a 4th",hood: "Reverchon Park",            count: 5 },
  { title: "Bayou kayak",            hood: "Bayou St. John, NOLA",      count: 9 },
  { title: "Vinyl club",             hood: "Deep Ellum, Dallas",        count: 4 },
  { title: "Photo walk",             hood: "French Quarter, NOLA",      count: 7 },
  { title: "Sunset rooftop",         hood: "Uptown, Dallas",            count: 11 },
  { title: "Run club 5K",            hood: "Katy Trail, Dallas",        count: 14 },
  { title: "Board game cafe",        hood: "Lakeview, NOLA",            count: 6 },
  { title: "Sound bath",             hood: "Bywater, NOLA",             count: 5 },
];

// Single pill card
const TickerCard = React.memo(function TickerCard({ a, isPulsing }) {
  return (
    <div
      className={`relative shrink-0 mx-3 inline-flex items-center gap-3 pl-3 pr-4 py-2.5 rounded-full bg-surface border transition-colors duration-200 ${
        isPulsing ? "border-primary/70 shadow-[0_0_0_3px_rgba(255,107,71,0.18)]" : "border-line"
      }`}
      data-testid="ticker-card"
    >
      {/* Live dot — flashes when pulsing */}
      <span className="relative flex w-2 h-2">
        <span
          className={`absolute inline-flex w-full h-full rounded-full ${
            isPulsing ? "bg-primary animate-ping" : "bg-primary/40"
          }`}
        />
        <span className="relative inline-flex w-2 h-2 rounded-full bg-primary" />
      </span>
      <span className="font-display font-bold text-base text-ink whitespace-nowrap">
        {a.title}
      </span>
      <span className="text-xs text-muted whitespace-nowrap">· {a.hood}</span>
      <motion.span
        className="inline-flex items-center gap-1 text-xs font-bold text-ink bg-secondary/80 px-2 py-0.5 rounded-full"
        animate={isPulsing ? { scale: [1, 1.12, 1] } : { scale: 1 }}
        transition={{ duration: 0.26, ease: EASE.snappyUI }}
      >
        <Users size={11} />
        <span>{a.count}</span>
      </motion.span>
    </div>
  );
});

export default function LiveTicker() {
  const reduce = useReducedMotion();
  const [pulseIdx, setPulseIdx] = useState(-1);

  // Random live pulse — picks a random card every 4-6s and pulses it briefly
  useEffect(() => {
    if (reduce) return;
    let timer;
    const schedule = () => {
      const wait = 4000 + Math.random() * 2000;
      timer = setTimeout(() => {
        const idx = Math.floor(Math.random() * ACTIVITIES.length);
        setPulseIdx(idx);
        setTimeout(() => setPulseIdx(-1), 1200);
        schedule();
      }, wait);
    };
    schedule();
    return () => clearTimeout(timer);
  }, [reduce]);

  // Double the cards for seamless infinite loop
  const cards = [...ACTIVITIES, ...ACTIVITIES];

  return (
    <div
      className="border-y border-line bg-surface-alt py-4 overflow-hidden group"
      data-testid="live-ticker"
      aria-label="Live activity feed"
    >
      <div className="ticker-track flex w-max">
        {cards.map((a, i) => (
          <TickerCard
            key={`${a.title}-${i}`}
            a={a}
            isPulsing={!reduce && pulseIdx === i % ACTIVITIES.length}
          />
        ))}
      </div>
      {/* Inject keyframes locally to keep this component self-contained */}
      <style>{`
        @keyframes fotr-ticker-slide {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .ticker-track {
          animation: fotr-ticker-slide 38s linear infinite;
          will-change: transform;
        }
        .group:hover .ticker-track { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .ticker-track { animation: none; }
        }
      `}</style>
    </div>
  );
}
